import {parse, stringify} from 'superjson';
import type {PersistStorage} from 'zustand/middleware';

/**
 * Creates a storage handler for persisting Zustand store state using localStorage.
 * Utilizes `superjson` for advanced serialization and deserialization capabilities, which
 * supports complex types like `Map`, `Set`, etc., ensuring accurate state reconstruction.
 * This function is designed to be used with Zustand's `persist` middleware.
 *
 * @template TStore The type of the store being persisted.
 * @returns An object that conforms to the `PersistStorage` interface for Zustand's middleware, providing methods to get, set, and remove items from localStorage.
 */
export const getStorage = <TStore>() => {
  return {
    /**
     * Retrieves an item from localStorage and parses it from JSON using SuperJSON.
     */
    getItem: name => {
      if (typeof localStorage === `undefined`) return null;
      const str = localStorage.getItem(name);
      if (!str) return null;
      return parse(str);
    },

    /**
     * Serializes the store state to JSON using SuperJSON and saves it in localStorage.
     * SuperJSON enhances the serialization process by handling complex types like `Map`,
     * which are not natively supported by JSON.stringify.
     */
    setItem: (name, value) => {
      if (typeof localStorage === `undefined`) return undefined;
      localStorage.setItem(name, stringify(value));
      return undefined;
    },

    /**
     * Removes an item from localStorage.
     */
    removeItem: name => {
      if (typeof localStorage === `undefined`) return undefined;
      localStorage.removeItem(name);
      return undefined;
    },
  } satisfies PersistStorage<TStore>;
};
